import {
ArrowRight,
Calculator,
Coffee,
DollarSign,
GraduationCap,
Heart,
Search,
X,
Zap
} from 'lucide-react';
import { useEffect,useRef,useState } from 'react';
import { useNavigate } from 'react-router-dom';
import calculators from '../lib/searchCalculators.json';

interface SearchBarProps {
  autoFocus?: boolean;
  onSelect?: () => void;
  className?: string;
}


const categories = [
  {
    id: 'financial',
    name: 'Financial Calculators',
    path: '/financial-calculators',
    icon: DollarSign,
    color: 'text-blue-500'
  },
  {
    id: 'health',
    name: 'Health Calculators',
    path: '/health-calculators',
    icon: Heart,
    color: 'text-rose-500'
  },
  {
    id: 'academic',
    name: 'Academic Calculators',
    path: '/academic-calculators',
    icon: GraduationCap,
    color: 'text-emerald-500'
  },
  {
    id: 'lifestyle',
    name: 'Lifestyle Calculators',
    path: '/lifestyle-calculators',
    icon: Coffee,
    color: 'text-amber-500'
  },
  {
    id: 'advanced',
    name: 'Advanced Calculators',
    path: '/advanced-calculators',
    icon: Zap,
    color: 'text-purple-500'
  },
  {
    id: 'utility',
    name: 'Utility Calculators',
    path: '/utility-calculators',
    icon: Calculator,
    color: 'text-cyan-500'
  }
];

export default function SearchBar({ autoFocus = false, onSelect, className = '' }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCalculators = query
    ? calculators.filter(calc =>
        calc.title.toLowerCase().includes(query.toLowerCase()) ||
        calc.category.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  const handleSelect = (path: string) => {
    navigate(path);
    setQuery('');
    setIsOpen(false);
    onSelect?.();
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="rounded-2xl p-[0.15rem] bg-gradient-to-r from-purple-500 to-pink-500">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsOpen(true)}
            className="w-full pl-10 pr-4 py-2.5 text-black bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
            placeholder="Search calculators..."
            autoFocus={autoFocus}
          />
        </div>
        <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 dark:text-gray-500" />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute mt-2 w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 max-h-[calc(100dvh-160px)] overflow-y-auto z-50 transition-all duration-200 transform">
          {query ? (
            <div className="p-2">
              {filteredCalculators.length > 0 ? (
                filteredCalculators.map((calc) => (
                  <button
                    key={calc.id}
                    onClick={() => handleSelect(calc.path)}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 group"
                  >
                    <div className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">{calc.title}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{calc.category}</div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No calculators found
                </div>
              )}
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => handleSelect(category.path)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`h-5 w-5 ${category.color}`} />
                      <span className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                        {category.name}
                      </span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transform group-hover:translate-x-1 transition-all duration-200" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
